/**
 * Vant 样式按路由拆分：
 * - 首页：仅首屏 + 城市/国家弹层用到的组件样式（体积远小于全量 ~200KB）
 * - 其它页 / 离开首页：全量 vant/lib/index.css
 *
 * 全量 Vant 必须排在所有样式之前：页面样式与 Vant 同优先级，
 * 若 Vant 后插入会反盖页面样式（如 .van-icon 的 position）。
 *
 * 副作用：首页已注入的 vant/es/popup 会晚于全量 CSS，把
 * .van-image-preview 的 width:100% 盖成 .van-popup--center 的 fit-content。
 * 补丁在 global.css：.van-popup.van-image-preview。
 */
import vantFullCssUrl from 'vant/lib/index.css?url'

const VANT_FULL_CSS_ID = 'vant-full-css'

const insertFullVantLink = () =>
  new Promise<void>((resolve, reject) => {
    if (document.getElementById(VANT_FULL_CSS_ID)) {
      resolve()
      return
    }
    const link = document.createElement('link')
    link.id = VANT_FULL_CSS_ID
    link.rel = 'stylesheet'
    link.href = vantFullCssUrl
    link.onload = () => resolve()
    link.onerror = () => {
      link.remove()
      reject(new Error(`load ${vantFullCssUrl} failed`))
    }
    const firstStyle = document.head.querySelector(
      'link[rel="stylesheet"], style',
    )
    document.head.insertBefore(link, firstStyle)
  })

let homeVantLoading: Promise<unknown> | null = null
let fullVantLoading: Promise<unknown> | null = null
let fullVantReady = false

/** 首页首屏 + 搜索弹层（MemberSearch / Header / CustomPopup） */
export const loadHomeVantCss = () => {
  if (fullVantReady) return Promise.resolve()
  if (homeVantLoading) return homeVantLoading
  homeVantLoading = Promise.all([
    import('vant/es/icon/style'),
    import('vant/es/popup/style'),
    import('vant/es/tabs/style'),
    import('vant/es/tab/style'),
    import('vant/es/search/style'),
    import('vant/es/button/style'),
    // 城市/国家/港口弹层列表
    import('vant/es/checkbox/style'),
    import('vant/es/checkbox-group/style'),
    import('vant/es/radio/style'),
    import('vant/es/radio-group/style'),
    import('vant/es/cell/style'),
    import('vant/es/cell-group/style'),
    import('vant/es/empty/style'),
    import('vant/es/loading/style'),
  ]).catch((err) => {
    homeVantLoading = null
    console.error('loadHomeVantCss failed', err)
  })
  return homeVantLoading
}

/** 全站其它页、或首页 idle / 离开首页后补齐 */
export const loadFullVantCss = () => {
  if (fullVantReady) return Promise.resolve()
  if (fullVantLoading) return fullVantLoading
  if (!import.meta.client) return Promise.resolve()
  fullVantLoading = insertFullVantLink()
    .then(() => {
      fullVantReady = true
    })
    .catch((err) => {
      fullVantLoading = null
      console.error('loadFullVantCss failed', err)
    })
  return fullVantLoading
}

export const ensureVantCssForRoute = async (isHome: boolean) => {
  if (isHome) {
    await loadHomeVantCss()
    return
  }
  if (import.meta.server) {
    // 仅服务端输出，客户端不接管，避免路由/布局切换时被移除
    useServerHead({
      link: [
        {
          id: VANT_FULL_CSS_ID,
          rel: 'stylesheet',
          href: vantFullCssUrl,
          tagPriority: 'critical',
        },
      ],
    })
    return
  }
  await loadFullVantCss()
}
